import { useState } from "react";

import { formatDistanceToNow } from "date-fns";

import { es } from "date-fns/locale";

import { useTranslation } from "react-i18next";

import { Badge } from "@/components/ui/badge";

import { Button } from "@/components/ui/button";

import { Card } from "@/components/ui/card";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import EmptyState from "../../../shared/components/EmptyState";

import LoadingOverlay from "../../../shared/components/LoadingOverlay";

import PageHeader from "../../../shared/components/PageHeader";

import { useSupportReportsQuery } from "../hooks/useSupport";

import type { SupportReport, SupportReportStatus } from "../types";

import SupportReportDetail from "./SupportReportDetail";

const STATUS_FILTERS: (SupportReportStatus | "all")[] = [
  "all",

  "new",

  "open",

  "pending",

  "resolved",

  "closed",
];

const PAGE_SIZE = 20;

const timeAgo = (iso: string) =>
  iso
    ? formatDistanceToNow(new Date(iso), { addSuffix: true, locale: es })
    : "—";

export const statusBadge = (status: string) => {
  const map: Record<string, string> = {
    new: "bg-yellow-500/20 text-yellow-400",

    open: "bg-blue-500/20 text-blue-400",

    pending: "bg-orange-500/20 text-orange-400",

    resolved: "bg-green-500/20 text-green-400",

    closed: "bg-neutral-500/20 text-neutral-300",
  };

  return (
    <Badge className={map[status] ?? "bg-neutral-500/20 text-neutral-300"}>
      {status}
    </Badge>
  );
};

const SupportReportsPage = () => {
  const { t } = useTranslation("common");

  const [status, setStatus] = useState<SupportReportStatus | "all">("all");

  const [page, setPage] = useState(1);

  const [selectedId, setSelectedId] = useState<string | null>(null);

  const query = {
    page,

    limit: PAGE_SIZE,

    ...(status !== "all" ? { status } : {}),
  };

  const reportsQuery = useSupportReportsQuery(query);

  const reports = reportsQuery.data?.data ?? [];

  const total = reportsQuery.data?.total ?? 0;

  const busy = reportsQuery.isLoading || reportsQuery.isFetching;

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title={t("SUPPORT.title")} subtitle={t("SUPPORT.subtitle")} />

      <div className="flex items-center gap-3">
        <select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as SupportReportStatus | "all");

            setPage(1);
          }}
          className="rounded-md border border-neutral-700 bg-neutral-900 px-3 py-1.5 text-sm text-neutral-200"
        >
          {STATUS_FILTERS.map((s) => (
            <option key={s} value={s}>
              {s === "all" ? t("SUPPORT.filterAll") : s}
            </option>
          ))}
        </select>
      </div>

      <Card className="relative border-neutral-800 bg-neutral-900/50">
        <Table>
          <TableHeader>
            <TableRow className="border-neutral-800 hover:bg-transparent">
              <TableHead className="text-neutral-400">
                {t("SUPPORT.colUser")}
              </TableHead>
              <TableHead className="text-neutral-400">
                {t("SUPPORT.colMessage")}
              </TableHead>
              <TableHead className="text-neutral-400">
                {t("SUPPORT.colSource")}
              </TableHead>
              <TableHead className="text-neutral-400">
                {t("SUPPORT.colStatus")}
              </TableHead>
              <TableHead className="text-neutral-400">
                {t("SUPPORT.colCreated")}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {reports.map((r: SupportReport) => (
              <TableRow
                key={r.id}
                className="cursor-pointer border-neutral-800 hover:bg-neutral-800/50"
                onClick={() => setSelectedId(r.id)}
              >
                <TableCell className="text-neutral-300">
                  {r.userSnapshot?.email ||
                    r.userSnapshot?.username ||
                    (r.userId ? `#${r.userId}` : t("SUPPORT.anonymous"))}
                </TableCell>
                <TableCell className="max-w-[420px] truncate text-neutral-200">
                  {r.message}
                </TableCell>
                <TableCell className="text-xs text-neutral-400">
                  {r.source}
                </TableCell>
                <TableCell>{statusBadge(r.status)}</TableCell>
                <TableCell className="text-sm text-neutral-400">
                  {timeAgo(r.createdAt)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {busy && <LoadingOverlay />}

        {reportsQuery.isError && (
          <div className="m-6 rounded-lg border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-300">
            {t("SUPPORT.loadError")}
          </div>
        )}

        {!reportsQuery.isLoading &&
          !reportsQuery.isError &&
          reports.length === 0 && (
            <EmptyState
              title={t("SUPPORT.emptyReportsTitle")}
              description={t("SUPPORT.emptyReportsDescription")}
              className="m-6"
            />
          )}

        <div className="flex items-center justify-between border-t border-neutral-800 px-4 py-3 text-xs text-neutral-400">
          <span>
            {t("SUPPORT.total")}: {total}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1 || busy}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              {t("SUPPORT.prev")}
            </Button>
            <span>
              {page} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages || busy}
              onClick={() => setPage((p) => p + 1)}
            >
              {t("SUPPORT.next")}
            </Button>
          </div>
        </div>
      </Card>

      <SupportReportDetail
        reportId={selectedId}
        open={!!selectedId}
        onOpenChange={(o) => !o && setSelectedId(null)}
      />
    </div>
  );
};

export default SupportReportsPage;
